import type {
  InfoBlock,
  InfoCard,
  InfoChartPoint,
  InfoFact,
  InfoFactTone,
  InfoListItem,
  InfoTimelineEvent,
} from '../workspace/workspace.types';

/**
 * Wire → model mapping for the ``present_info`` ui action. The BE already
 * normalises model-authored cards (`ml-scribe/service/voice/workspace_info.py`),
 * but the FE re-validates fail-closed — a malformed block is dropped, and a
 * card with nothing renderable maps to ``null`` so the caller logs and drops
 * it rather than appending an empty card to the feed.
 */

const FACT_TONES: readonly InfoFactTone[] = [
  'default',
  'positive',
  'warning',
  'critical',
];

const MAX_BLOCKS = 12;
const MAX_FACTS = 12;
const MAX_COLUMNS = 6;
const MAX_ROWS = 20;
const MAX_SERIES = 24;
const MAX_ITEMS = 12;
const MAX_EVENTS = 12;
const MAX_FOLLOW_UPS = 4;

const cleanStr = (value: unknown, cap: number): string =>
  typeof value === 'string' ? value.trim().slice(0, cap) : '';

const cleanNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const toFacts = (raw: unknown): InfoFact[] => {
  if (!Array.isArray(raw)) return [];
  const facts: InfoFact[] = [];
  for (const item of raw.slice(0, MAX_FACTS)) {
    if (!item || typeof item !== 'object') continue;
    const record = item as Record<string, unknown>;
    const label = cleanStr(record.label, 120);
    const value = cleanStr(record.value, 160);
    if (!label || !value) continue;
    const hint = cleanStr(record.hint, 160);
    const tone = FACT_TONES.includes(record.tone as InfoFactTone)
      ? (record.tone as InfoFactTone)
      : undefined;
    facts.push({
      label,
      value,
      ...(hint ? { hint } : {}),
      ...(tone && tone !== 'default' ? { tone } : {}),
    });
  }
  return facts;
};

const toTable = (
  rawColumns: unknown,
  rawRows: unknown
): { columns: string[]; rows: string[][] } | null => {
  if (!Array.isArray(rawColumns) || !Array.isArray(rawRows)) return null;
  const columns = rawColumns
    .slice(0, MAX_COLUMNS)
    .map((col) => cleanStr(col, 120))
    .filter(Boolean);
  if (columns.length < 2) return null;
  const rows: string[][] = [];
  for (const rawRow of rawRows.slice(0, MAX_ROWS)) {
    if (!Array.isArray(rawRow)) continue;
    const row = rawRow.slice(0, columns.length).map((cell) => cleanStr(cell, 160));
    while (row.length < columns.length) row.push('');
    if (row.some(Boolean)) rows.push(row);
  }
  return rows.length > 0 ? { columns, rows } : null;
};

const toSeries = (raw: unknown): InfoChartPoint[] => {
  if (!Array.isArray(raw)) return [];
  const series: InfoChartPoint[] = [];
  for (const point of raw.slice(0, MAX_SERIES)) {
    if (!point || typeof point !== 'object') continue;
    const record = point as Record<string, unknown>;
    const label = cleanStr(record.label, 40);
    const value = cleanNumber(record.value);
    if (!label || value === undefined) continue;
    series.push({ label, value });
  }
  return series;
};

const toItems = (raw: unknown): InfoListItem[] => {
  if (!Array.isArray(raw)) return [];
  const items: InfoListItem[] = [];
  for (const item of raw.slice(0, MAX_ITEMS)) {
    if (!item || typeof item !== 'object') continue;
    const record = item as Record<string, unknown>;
    const text = cleanStr(record.text, 2000);
    if (!text) continue;
    const detail = cleanStr(record.detail, 2000);
    items.push({ text, ...(detail ? { detail } : {}) });
  }
  return items;
};

const toEvents = (raw: unknown): InfoTimelineEvent[] => {
  if (!Array.isArray(raw)) return [];
  const events: InfoTimelineEvent[] = [];
  for (const item of raw.slice(0, MAX_EVENTS)) {
    if (!item || typeof item !== 'object') continue;
    const record = item as Record<string, unknown>;
    const date = cleanStr(record.date, 40);
    const text = cleanStr(record.text, 2000);
    if (!date || !text) continue;
    const detail = cleanStr(record.detail, 2000);
    events.push({ date, text, ...(detail ? { detail } : {}) });
  }
  return events;
};

const toBlock = (raw: unknown, allowDetails: boolean): InfoBlock | null => {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const title = cleanStr(record.title, 120);

  switch (record.type) {
    case 'text': {
      const body = cleanStr(record.body, 2000);
      if (!body) return null;
      return { type: 'text', ...(title ? { title } : {}), body };
    }
    case 'facts': {
      const facts = toFacts(record.facts);
      if (facts.length === 0) return null;
      return { type: 'facts', ...(title ? { title } : {}), facts };
    }
    case 'table': {
      const table = toTable(record.columns, record.rows);
      if (!table) return null;
      return { type: 'table', ...(title ? { title } : {}), ...table };
    }
    case 'chart': {
      const series = toSeries(record.series);
      if (series.length < 2) return null;
      const unit = cleanStr(record.unit, 20);
      const referenceLow = cleanNumber(record.reference_low);
      const referenceHigh = cleanNumber(record.reference_high);
      const referenceLabel = cleanStr(record.reference_label, 120);
      return {
        type: 'chart',
        ...(title ? { title } : {}),
        chartKind: record.chart_kind === 'bar' ? 'bar' : 'line',
        ...(unit ? { unit } : {}),
        series,
        ...(referenceLow !== undefined ? { referenceLow } : {}),
        ...(referenceHigh !== undefined ? { referenceHigh } : {}),
        ...(referenceLabel && (referenceLow !== undefined || referenceHigh !== undefined)
          ? { referenceLabel }
          : {}),
      };
    }
    case 'list': {
      const items = toItems(record.items);
      if (items.length === 0) return null;
      return {
        type: 'list',
        ...(title ? { title } : {}),
        ...(record.list_style === 'numbered' ? { listStyle: 'numbered' as const } : {}),
        items,
      };
    }
    case 'timeline': {
      const events = toEvents(record.events);
      if (events.length === 0) return null;
      return { type: 'timeline', ...(title ? { title } : {}), events };
    }
    case 'details': {
      if (!allowDetails || !title) return null;
      // The BE nests details content as `blocks`; recurse exactly one level.
      const rawBlocks = Array.isArray(record.blocks) ? record.blocks : [];
      const blocks = rawBlocks
        .slice(0, MAX_BLOCKS)
        .map((inner) => toBlock(inner, false))
        .filter((inner): inner is InfoBlock => inner !== null);
      if (blocks.length === 0) return null;
      return { type: 'details', title, blocks };
    }
    default:
      return null;
  }
};

let fallbackIdSeq = 0;

/**
 * Wire payload of the ``present_info`` ui action as this module accepts it —
 * laxer than ``protocol.ts``'s `VoicePresentInfoData` because the mapper
 * treats every field as untrusted.
 */
export type VoiceUiPresentInfoData = {
  card?: {
    id?: string;
    title?: string;
    subtitle?: string;
    tone?: string;
    blocks?: unknown[];
    follow_ups?: unknown[];
  };
};

/**
 * Map a ``present_info`` wire payload to an {@link InfoCard}. Returns
 * ``null`` when nothing renderable survives validation.
 */
export const mapPresentInfoData = (data: VoiceUiPresentInfoData): InfoCard | null => {
  const card = data?.card;
  if (!card || typeof card !== 'object') return null;
  const title = cleanStr(card.title, 120);
  if (!title) return null;

  const blocks = (Array.isArray(card.blocks) ? card.blocks : [])
    .slice(0, MAX_BLOCKS)
    .map((raw) => toBlock(raw, true))
    .filter((block): block is InfoBlock => block !== null);
  if (blocks.length === 0) return null;

  const subtitle = cleanStr(card.subtitle, 120);
  const followUps = (Array.isArray(card.follow_ups) ? card.follow_ups : [])
    .slice(0, MAX_FOLLOW_UPS)
    .map((item) => cleanStr(item, 80))
    .filter(Boolean);

  return {
    kind: 'generic',
    id: cleanStr(card.id, 40) || `info-${Date.now()}-${fallbackIdSeq++}`,
    title,
    ...(subtitle ? { subtitle } : {}),
    ...(card.tone === 'attention' ? { tone: 'attention' as const } : {}),
    blocks,
    ...(followUps.length > 0 ? { followUps } : {}),
    ts: Date.now(),
  };
};
